import { useState, useCallback } from 'react';
import { Button, Input } from '@kollektive/design-system';
import { Plus, Search, X, Globe, Trash2 } from 'lucide-react';
import { useConnections } from '@/hooks/useConnections';
import { useIntegrations } from '@/hooks/useIntegrations';
import type { Integration } from '@/hooks/useIntegrations';
import {
  ConnectionTable,
  ConnectionFormModal,
  ConnectionTypeCatalog,
  ConnectionTypeCatalogModal,
  AtlassianConnectionModal,
} from '@/components/connections';
import { DeleteConnectionDialog } from '@/components/connections/DeleteConnectionDialog';
import type { ConnectionTypeDefinition } from '@/components/connections/connectionTypeRegistry';
import type { Connection, DatabaseDialect, CreateConnectionRequest, UpdateConnectionRequest } from '@/types';
import styles from './ConnectionsPage.module.css';

export function ConnectionsPage() {
  const {
    connections,
    isLoading,
    error,
    refresh,
    createConnection,
    updateConnection,
    deleteConnection,
    testConnection,
    testExistingConnection,
  } = useConnections();

  const {
    integrations,
    isLoading: integrationsLoading,
    deleteIntegration,
  } = useIntegrations();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const [connectionToDelete, setConnectionToDelete] = useState<Connection | null>(null);
  const [connectionsToDelete, setConnectionsToDelete] = useState<Connection[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [showAtlassianModal, setShowAtlassianModal] = useState(false);
  const [preselectedDialect, setPreselectedDialect] = useState<DatabaseDialect | undefined>();
  const [deletingIntegrationId, setDeletingIntegrationId] = useState<string | null>(null);

  const allLoading = isLoading || integrationsLoading;
  const hasConnections = !allLoading && (connections.length > 0 || integrations.length > 0);

  const handleTypeSelect = useCallback((type: ConnectionTypeDefinition) => {
    setShowCatalogModal(false);
    if (type.kind === 'database' && type.dialect) {
      setPreselectedDialect(type.dialect);
      setEditingConnection(null);
      setIsModalOpen(true);
    } else if (type.kind === 'atlassian') {
      setShowAtlassianModal(true);
    }
  }, []);

  const handleAddClick = useCallback(() => {
    if (hasConnections) {
      setShowCatalogModal(true);
    }
  }, [hasConnections]);

  const handleEditClick = useCallback((connection: Connection) => {
    setPreselectedDialect(undefined);
    setEditingConnection(connection);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setEditingConnection(null);
    setPreselectedDialect(undefined);
  }, []);

  const handleSave = useCallback(async (data: CreateConnectionRequest | UpdateConnectionRequest) => {
    if (editingConnection) {
      await updateConnection(editingConnection.id, data as UpdateConnectionRequest);
    } else {
      await createConnection(data as CreateConnectionRequest);
    }
  }, [editingConnection, createConnection, updateConnection]);

  const handleDeleteClick = useCallback((connection: Connection) => {
    setConnectionToDelete(connection);
  }, []);

  const handleBulkDelete = useCallback((connections: Connection[]) => {
    setConnectionsToDelete(connections);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      if (connectionToDelete) {
        await deleteConnection(connectionToDelete.id);
        setConnectionToDelete(null);
      } else if (connectionsToDelete.length > 0) {
        await Promise.all(connectionsToDelete.map((c) => deleteConnection(c.id)));
        setConnectionsToDelete([]);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [connectionToDelete, connectionsToDelete, deleteConnection]);

  const handleDeleteCancel = useCallback(() => {
    setConnectionToDelete(null);
    setConnectionsToDelete([]);
  }, []);

  const showDeleteDialog = connectionToDelete !== null || connectionsToDelete.length > 0;
  const deleteDialogConnection = connectionToDelete ?? connectionsToDelete[0];
  const isBulkDelete = connectionsToDelete.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Connections</h1>
          <p className={styles.subtitle}>
            Manage your connections for AI tools
          </p>
        </div>
        {hasConnections && (
          <div className={styles.headerActions}>
            <div className={styles.searchWrapper}>
              <Input
                placeholder="Search connections..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setSearchQuery('')}
                iconLeft={<Search size={16} />}
                iconRight={
                  searchQuery ? (
                    <button
                      className={styles.clearButton}
                      onClick={() => setSearchQuery('')}
                      type="button"
                    >
                      <X size={14} />
                    </button>
                  ) : undefined
                }
                className={styles.searchInput}
              />
            </div>
            <Button
              variant="primary"
              onClick={handleAddClick}
              title="Add Connection"
              className={styles.iconButton}
            >
              <Plus size={20} />
            </Button>
          </div>
        )}
      </div>

      {!allLoading && !error && connections.length === 0 && integrations.length === 0 ? (
        <ConnectionTypeCatalog onSelect={handleTypeSelect} />
      ) : (
        <>
          {integrations.length > 0 && (
            <div className={styles.integrationsSection}>
              {integrations.map((integration: Integration) => (
                <div key={integration.id} className={styles.integrationCard}>
                  <div className={styles.integrationIcon}>
                    <Globe size={20} style={{ color: '#0052CC' }} />
                  </div>
                  <div className={styles.integrationInfo}>
                    <span className={styles.integrationName}>{integration.name}</span>
                    <span className={styles.integrationDetail}>
                      {integration.site_url || integration.provider} &middot; {integration.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <button
                    className={styles.integrationDelete}
                    onClick={async () => {
                      setDeletingIntegrationId(integration.id);
                      try {
                        await deleteIntegration(integration.id);
                      } finally {
                        setDeletingIntegrationId(null);
                      }
                    }}
                    disabled={deletingIntegrationId === integration.id}
                    title="Disconnect"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <ConnectionTable
            connections={connections}
            isLoading={isLoading}
            error={error}
            onRetry={refresh}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onBulkDelete={handleBulkDelete}
            onTest={testExistingConnection}
            isDeleting={isDeleting}
            searchQuery={searchQuery}
          />
        </>
      )}

      {/* Catalog Modal (when connections exist and user clicks "+") */}
      <ConnectionTypeCatalogModal
        open={showCatalogModal}
        onClose={() => setShowCatalogModal(false)}
        onSelect={handleTypeSelect}
      />

      {/* Atlassian Connection Modal */}
      <AtlassianConnectionModal
        open={showAtlassianModal}
        onClose={() => setShowAtlassianModal(false)}
      />

      {/* Connection Form Modal */}
      <ConnectionFormModal
        open={isModalOpen}
        onClose={handleModalClose}
        connection={editingConnection}
        onSave={handleSave}
        onDelete={(id) => deleteConnection(id)}
        onTest={testConnection}
        preselectedDialect={preselectedDialect}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && deleteDialogConnection && (
        <DeleteConnectionDialog
          connection={deleteDialogConnection}
          connectionCount={isBulkDelete ? connectionsToDelete.length : 1}
          isOpen={true}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
}
